import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '@/store/cart';
import { useAuth } from '@/contexts/AuthContext';
import { CartItem } from '@/components/cart/CartItem';
import { CartStoreInfo } from '@/components/cart/CartStoreInfo';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';
import { cartService } from '@/services/cart';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';

export function DesktopCartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  const {
    storeId,
    storeName,
    orderType,
    items,
    usedPoints,
    desiredPickupTime,
    setUsedPoints,
    getTotalPrice,
    getFinalPrice,
    canAddMoreItems,
  } = useCartStore();
  const isDeadlineOrder = orderType === 'DEADLINE_DISCOUNT';

  const [userPoints, setUserPoints] = useState(0);
  const [pointInput, setPointInput] = useState('');
  const [isLoadingPoints, setIsLoadingPoints] = useState(true);
  const [restaurantInfo, setRestaurantInfo] = useState<Restaurant | null>(null);
  const [isLoadingRestaurant, setIsLoadingRestaurant] = useState(true);
  const [isValidatingCart, setIsValidatingCart] = useState(false);

  // Validate cart stock on page load
  useEffect(() => {
    const validateCartStock = async () => {
      if (!user?.id || !storeId || items.length === 0) return;
      
      setIsValidatingCart(true);
      try {
        const cart = await cartService.getCurrentCart(user.id);
        if (cart) {
          const validation = await cartService.validateCartStock(cart.id);
          
          if (!validation.success && validation.unavailableItems.length > 0) {
            showToast(`다음 상품의 재고가 부족하여 장바구니에서 제거되었습니다: ${validation.unavailableItems.join(', ')}`, 4000);
          }
        }
      } catch (error) {
        console.error('Cart validation error:', error);
      } finally {
        setIsValidatingCart(false);
      }
    };

    validateCartStock();
  }, [user?.id, storeId, items.length, showToast]);

  // Fetch user points
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!user?.id) {
        setIsLoadingPoints(false);
        return;
      }

      try {
        const { data: pointsData, error: pointsError } = await supabase
          .from('user_points')
          .select('current_balance')
          .eq('user_id', user.id)
          .single();

        if (pointsError) {
          console.error('Failed to fetch points:', pointsError);
          setUserPoints(0);
        } else {
          setUserPoints(pointsData?.current_balance || 0);
        }
      } catch (error) {
        console.error('Error fetching points:', error);
        setUserPoints(0);
      } finally {
        setIsLoadingPoints(false);
      }
    };

    fetchUserPoints();
  }, [user]);

  // Fetch restaurant info
  useEffect(() => {
    const fetchRestaurantInfo = async () => {
      if (!storeId) {
        setIsLoadingRestaurant(false);
        return;
      }

      try {
        const result = await restaurantService.getRestaurantById(storeId);
        if (result.success && result.data) {
          setRestaurantInfo(result.data);
        } else {
          console.error('Failed to fetch restaurant:', result.error);
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
      } finally {
        setIsLoadingRestaurant(false);
      }
    };

    fetchRestaurantInfo();
  }, [storeId]);

  // Initialize point input
  useEffect(() => {
    setPointInput(usedPoints.toString());
  }, [usedPoints]);

  useEffect(() => {
    if (!isDeadlineOrder) return;

    if (usedPoints !== 0) {
      setUsedPoints(0);
    }

    if (pointInput !== '') {
      setPointInput('');
    }
  }, [isDeadlineOrder, usedPoints, pointInput, setUsedPoints]);

  const handlePointInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isDeadlineOrder) return;

    if (!/^\d*$/.test(value)) return;
    
    const points = parseInt(value) || 0;
    const maxPoints = Math.min(userPoints, getTotalPrice());
    
    if (points <= maxPoints) {
      setPointInput(value);
      setUsedPoints(points);
    } else {
      setPointInput(maxPoints.toString());
      setUsedPoints(maxPoints);
      showToast(`최대 ${maxPoints.toLocaleString()}포인트까지 사용 가능합니다`);
    }
  };

  const handleAddMore = () => {
    if (!canAddMoreItems()) {
      showToast('장바구니에는 최대 5개의 다른 상품만 담을 수 있습니다');
      return;
    }
    navigate(`/store/${storeId}`);
  };

  const handleCheckout = async () => {
    if (items.length === 0) {
      showToast('장바구니가 비어있습니다');
      return;
    }

    // Real-time stock validation before checkout
    if (user?.id) {
      try {
        setIsValidatingCart(true);
        const cart = await cartService.getCurrentCart(user.id);
        
        if (cart) {
          const validation = await cartService.validateCartStock(cart.id);
          
          if (!validation.success) {
            showToast(validation.error || '일부 상품의 재고가 부족합니다', 3000);
            return;
          }
        }
      } catch (error) {
        console.error('Checkout validation error:', error);
        showToast('재고 확인 중 오류가 발생했습니다', 3000);
        return;
      } finally {
        setIsValidatingCart(false);
      }
    }

    navigate('/checkout', {
      state: {
        storeId,
        storeName,
        orderType,
        items,
        usedPoints,
        desiredPickupTime,
        totalPrice: getTotalPrice(),
        finalPrice: getFinalPrice(userPoints),
      }
    });
  };

  const totalPrice = getTotalPrice();
  const finalPrice = getFinalPrice(userPoints);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  if (!storeId || items.length === 0) {
    return (
      <DesktopLayout>
        <div className="max-w-6xl mx-auto py-8">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h1 className="text-3xl font-bold mb-8 text-center">장바구니</h1>
            
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-32 h-32 mb-6 text-gray-300">
                <svg width="128" height="128" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M3 3H5L6.5 9M6.5 9H17.5L21 3M6.5 9L8 16H16L17.5 9M6.5 9H17.5M8 16C6.89543 16 6 16.8954 6 18C6 19.1046 6.89543 20 8 20C9.10457 20 10 19.1046 10 18C10 16.8954 9.10457 16 8 16ZM16 16C14.8954 16 14 16.8954 14 18C14 19.1046 14.8954 20 16 20C17.1046 20 18 19.1046 18 18C18 16.8954 17.1046 16 16 16Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="text-xl text-gray-600 mb-8">장바구니가 비어있습니다</p>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-green-500 text-white rounded-xl font-semibold text-lg hover:bg-green-600 transition-colors"
              >
                상품 보러가기
              </button>
            </div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="max-w-6xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-8">장바구니</h1>
        
        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Cart Items (2/3 width) */}
          <div className="col-span-2 space-y-6">
            {/* Store Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <CartStoreInfo 
                storeId={storeId} 
                restaurantInfo={restaurantInfo}
                isLoading={isLoadingRestaurant}
              />
            </div>

            {/* Cart Items */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">주문 상품</h2>
              <div className="space-y-4">
                {items.map((item) => (
                  <CartItem
                    key={item.productId}
                    item={item}
                  />
                ))}
                
                {/* Add More Button */}
                {canAddMoreItems() && (
                  <button
                    onClick={handleAddMore}
                    className="w-full py-4 bg-[#F5F5F5] rounded-xl flex items-center justify-center gap-3 hover:bg-gray-200 transition-colors"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5V19M5 12H19" stroke="#222222" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span className="text-lg font-medium text-[#222222]">주문 추가</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Payment Info (1/3 width) */}
          <div className="col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-semibold mb-6">결제 정보</h2>
              
              {/* Point Usage */}
              {user && !isLoadingPoints && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium mb-4">포인트 사용</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">보유 포인트</span>
                      <span className="font-medium">{userPoints.toLocaleString()}P</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={pointInput}
                        onChange={handlePointInputChange}
                        placeholder="0"
                        disabled={isDeadlineOrder}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-right text-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      />
                      <span className="text-gray-600 text-lg">P</span>
                    </div>
                    {isDeadlineOrder && (
                      <p className="text-xs text-gray-500">마감할인 상품은 포인트 적립만 가능해요.</p>
                    )}
                    
                    <button
                      onClick={() => {
                        const maxPoints = Math.min(userPoints, getTotalPrice());
                        setPointInput(maxPoints.toString());
                        setUsedPoints(maxPoints);
                      }}
                      disabled={isDeadlineOrder}
                      aria-disabled={isDeadlineOrder}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      전액사용
                    </button>
                  </div>
                </div>
              )}

              {/* Price Summary */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">상품 금액</span>
                  <span className="font-medium text-lg">{totalPrice.toLocaleString()}원</span>
                </div>
                {usedPoints > 0 && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>포인트 할인</span>
                    <span className="font-medium">-{usedPoints.toLocaleString()}원</span>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">결제 예정 금액</span>
                    <span className="text-2xl font-bold text-green-600">
                      {finalPrice.toLocaleString()}원
                    </span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                disabled={isValidatingCart}
                className={`w-full py-4 rounded-xl font-semibold text-lg transition-colors ${
                  isValidatingCart 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {isValidatingCart ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>재고 확인 중...</span>
                  </div>
                ) : (
                  <span>{finalPrice.toLocaleString()}원 결제하기</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopLayout>
  );
}
